import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings, Bell, FlaskConical, Shield, FileText,
  ChevronRight, LogOut, ExternalLink, FileCheck2,
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { BrandFooter } from '@/shared/components/BrandFooter';
import { useAuth } from '@/auth/AuthProvider';
import { useAgreementStore } from '@/modules/agreements/store/agreementStore';
import { useAgreements } from '@/modules/agreements/hooks/useAgreements';
import oidcConfig from '@/auth/oidcConfig';

function getInitials(name: string | undefined, email: string | undefined): string {
  if (name) {
    return name
      .split(' ')
      .slice(0, 2)
      .map(w => w[0])
      .join('')
      .toUpperCase();
  }
  if (email) return email[0].toUpperCase();
  return '?';
}

function NavRow({
  icon,
  label,
  description,
  onClick,
  external,
}: {
  icon: React.ReactNode;
  label: string;
  description?: string;
  onClick: () => void;
  external?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-slate-50 active:bg-slate-100"
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        {description && <p className="text-xs text-slate-500">{description}</p>}
      </div>
      {external
        ? <ExternalLink className="h-4 w-4 shrink-0 text-slate-300" aria-hidden="true" />
        : <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" aria-hidden="true" />
      }
    </button>
  );
}

const ProfilePage: React.FC = () => {
  useEffect(() => { document.title = 'Perfil'; }, []);

  const auth = useAuth();
  const navigate = useNavigate();
  const { activeAgreement, setActiveAgreement } = useAgreementStore();
  const { data: agreements = [] } = useAgreements();

  const name = auth.user?.profile?.name as string | undefined;
  const email = auth.user?.profile?.email as string | undefined;
  const initials = getInitials(name, email);

  const handleAccount = () => {
    const url = `${oidcConfig.authority.replace(/\/$/, '')}/account`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <main className="space-y-4 px-4 py-6" id="main-content" role="main" aria-label="Perfil">

      {/* User card */}
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-5">
        <div className="flex items-center gap-4">
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-slate-900 text-lg font-bold text-white"
            aria-hidden="true"
          >
            {initials}
          </div>
          <div className="min-w-0">
            {name && (
              <p className="truncate text-base font-semibold text-slate-900">{name}</p>
            )}
            {email && (
              <p className="truncate text-sm text-slate-500">{email}</p>
            )}
            {!name && !email && (
              <p className="text-sm text-slate-400">Usuário autenticado</p>
            )}
          </div>
        </div>
      </div>

      {/* Active agreement */}
      {activeAgreement && (
        <div className="rounded-2xl border border-slate-200 bg-white">
          <div className="flex items-center gap-3 px-5 pb-3 pt-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
              <FileCheck2 className="h-4 w-4" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-500">Acordo ativo</p>
              <p className="text-sm font-semibold text-slate-900 truncate">
                {activeAgreement.hashid}
                <span className="ml-1.5 font-normal text-slate-400">({activeAgreement.state})</span>
              </p>
            </div>
          </div>

          {agreements.length > 1 && (
            <div className="px-5 pb-4">
              <label className="mb-1 block text-xs font-medium text-slate-500">
                Trocar acordo
              </label>
              <select
                value={activeAgreement.uuid}
                onChange={e => {
                  const found = agreements.find(a => a.uuid === e.target.value);
                  if (found) setActiveAgreement(found);
                }}
                aria-label="Selecionar acordo ativo"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
              >
                {agreements.map(a => (
                  <option key={a.uuid} value={a.uuid}>
                    {a.hashid} ({a.state})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Configurações */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center gap-2 px-5 pb-2 pt-4">
          <Settings className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Configurações
          </p>
        </div>
        <NavRow
          icon={<Settings className="h-4 w-4" />}
          label="Preferências"
          description="Tema, idioma e notificações"
          onClick={() => navigate('/prefs')}
        />
        <Separator className="mx-5" />
        <NavRow
          icon={<Bell className="h-4 w-4" />}
          label="Notificações"
          description="TTS, lembretes e preferências"
          onClick={() => navigate('/notifications/settings')}
        />
        <Separator className="mx-5" />
        <NavRow
          icon={<FlaskConical className="h-4 w-4" />}
          label="Laboratório"
          description="Recursos experimentais e debug"
          onClick={() => navigate('/labs')}
        />
      </div>

      {/* Privacidade e conta */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center gap-2 px-5 pb-2 pt-4">
          <Shield className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Privacidade e conta
          </p>
        </div>
        <NavRow
          icon={<Shield className="h-4 w-4" />}
          label="Privacidade"
          description="Consentimentos e seus dados"
          onClick={() => navigate('/privacy')}
        />
        <Separator className="mx-5" />
        <NavRow
          icon={<FileText className="h-4 w-4" />}
          label="Termos de uso"
          onClick={() => navigate('/terms')}
        />
        <Separator className="mx-5" />
        <NavRow
          icon={<FileText className="h-4 w-4" />}
          label="Política de Privacidade"
          onClick={() => navigate('/privacy-policy')}
        />
        <Separator className="mx-5" />
        <NavRow
          icon={<ExternalLink className="h-4 w-4" />}
          label="Minha conta"
          description="Gerenciar na Plataforma de identidade"
          onClick={handleAccount}
          external
        />
      </div>

      {/* Logout */}
      <div className="pb-2">
        <Button
          variant="outline"
          className="w-full gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
          onClick={() => auth.signoutRedirect()}
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          Sair da conta
        </Button>
      </div>

      <div className="flex justify-center pb-4">
        <BrandFooter />
      </div>

    </main>
  );
};

export default ProfilePage;
